import { getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table';
import type { OnChangeFn, PaginationState, SortingState, Updater, ColumnDef } from '@tanstack/react-table';
import * as React from 'react';
import { ActionBar } from './components/action-bar';
import { DataGridBody } from './components/body';
import { createSelectionColumn } from './components/columns/selection';
import { DataGridFooter } from './components/grid/data-grid-footer';
import { DataGridHeader } from './components/headers/header';
import { LoadingOverlay } from './components/overlays/loading';
import { NoColumnsOverlay } from './components/overlays/no-columns';
import {
	DEFAULT_META,
	DEFAULT_OPTIONS,
	DEFAULT_PAGE_SIZE,
	EMPTY_STATE_MIN_HEIGHT_PX,
	HEADER_HEIGHT,
	PAGE_SIZE_SYNC_DEBOUNCE_MS,
} from './data-grid.constants';
import type {
	DataGridActionColumnDef,
	DataGridBaseRow,
	DataGridProps,
	DataGridRow,
	DataGridSelectedRowsMetadata,
} from './data-grid.type';
import { useDataGrid } from './hooks/use-data-grid';
import { useDataGridUrlSync } from './hooks/use-data-grid-url-sync';
import useDebounce from './hooks/use-debounce';
import { useOptimalPageSize } from './util/page-size';
import {
	getActionableItemCount,
	getRowsMinHeight,
	getSelectedRows,
	isRowSelected,
	resolveRowSelectionDisabled,
} from './util/rows';
import { toUrlCalendarDate } from './util/url-sync';
import { OrderByEnum, TablePrimitive, TablePrimitiveContainer } from '@components/organisms';

/**
 * A flexible data grid component for displaying tabular data with advanced features including
 * sorting, pagination, row selection, filtering, and customizable columns.
 *
 * @component
 * @template T - The type of data being displayed in the grid rows
 * @template CD - Optional custom data type for column definitions (defaults to any)
 *
 * @property {DataGridActionBarProps} [actionBarProps] - Configuration for the action bar above the grid
 *   Controls visibility and behavior of search, filters, column visibility, and custom actions
 *
 * @property {any[]} [apiArgs] - Arguments to pass to the apiMethod when fetching data
 *   Used for API-driven data loading scenarios
 *
 * @property {Function} [apiMethod] - Method to call for fetching data from an API
 *   Should return a Promise resolving to an array of type T
 *
 * @property {Function} [apiResponseParser] - Function to transform API response into row data
 *   Converts raw API data into the expected format for grid rows
 *
 * @property {DataGridColumnDef<CD>[]} columns - Column definitions for the grid
 *   Specifies how to display and interact with each column
 *
 * @property {T[]} [data] - Static data to display in the grid
 *   Used when not fetching data via API
 *
 * @property {string} [defaultSortColumn] - Column key to sort by initially
 *
 * @property {OrderByEnum} [defaultSortDirection] - Initial sort direction (ASC/DESC)
 *
 * @property {APIMeta} [meta] - Metadata about the current data state
 *   Includes pagination info, total counts, etc.
 *
 * @property {number} [pageSize] - Number of rows to display per page
 *   Defaults to DEFAULT_PAGE_SIZE constant
 *
 * @property {number} [rowCount] - Total number of rows across all pages
 *   Used for pagination calculations
 *
 * @property {'single' | 'multiple'} [rowSelection] - Enables row selection mode
 *   - 'single': Only one row can be selected at a time
 *   - 'multiple': Multiple rows can be selected simultaneously
 *
 * @property {DataGridStrings} [strings] - Customizable text strings for the grid
 *   Includes labels, messages, and aria text
 *
 * @property {(error: Error) => void} [onError] - Handler for error states
 *   Called when data loading or other operations fail
 *
 * @property {(isLoading: boolean) => void} [onLoadingChange] - Loading state change handler
 *   Called whenever the loading state changes
 *
 * @property {(newPagination: PaginationState) => void} [onPaginationChange] - Pagination change handler
 *   Called when page number or page size changes
 *
 * @property {(refetch: () => Promise<void>) => void} [onRefetchChange] - Exposes data refetch function
 *   Allows parent components to trigger data refresh
 *
 * @property {(newSorting: SortingState) => void} [onSortingChange] - Sort change handler
 *   Called when sort column or direction changes
 *
 * @property {(row: T) => void | null} [onRowClick] - Row click handler
 * @default - The first action column's onClick handler
 * @note - To disable default behavior, set to null
 * @description Called when a row is clicked
 *
 * @property {(rows: T[]) => void} [onSelectRows] - Row selection handler
 *   Called with currently selected rows when selection changes
 *
 * @property {boolean} [withSelectAll]
 * Whether to include a "Select All Rows" checkbox in the header
 *
 * @property {(newState: DataGridState<T>) => void} [onStateChange] - Grid state change handler
 *   Called whenever any grid state changes (sorting, filtering, etc.)
 *
 * @property {() => Promise<DataGridFilterFetcherResponse>} [filtersFetcher] - Function to fetch filters
 *   Called when the grid is initialized to fetch filters
 *
 * @property {string} [uniqueField] - Unique field to use for row identification
 * @default 'id'
 *
 * @property {DataGridRowsHeight | number} [rowsHeight] - The fixed height of every row
 * @description Rows are clamped to this height (taller content is clipped) so the page size is
 * stable across pages. Use 'lg' when cells need more than ~two lines (e.g. tooltip buttons), or
 * pass an explicit pixel value when a cell (e.g. a media thumbnail) is taller than the token.
 * @default 'md'
 *
 * @example
 * // Basic usage with static data
 * <DataGrid
 *   columns={[
 *     { key: 'name', header: 'Name', type: 'text' },
 *     { key: 'age', header: 'Age', type: 'number' }
 *   ]}
 *   data={[
 *     { name: 'John', age: 30 },
 *     { name: 'Jane', age: 25 }
 *   ]}
 * />
 *
 * @example
 * // With API integration and row selection
 * <DataGrid
 *   apiMethod={fetchUsers}
 *   apiResponseParser={parseUserData}
 *   columns={userColumns}
 *   rowSelection="multiple"
 *   onSelectRows={handleUserSelection}
 *   pageSize={25}
 * />
 *
 * @example
 * // With action bar and custom empty state
 * <DataGrid
 *   columns={documentColumns}
 *   actionBarProps={{
 *     showSearch: true,
 *     showFilterMenu: true,
 *     customActions: <Button>Custom Action</Button>
 *   }}
 *   emptyState="No documents found"
 *   onRowClick={handleDocumentClick}
 * />
 *
 * @example
 * // With custom column formatting
 * <DataGrid
 *   columns={[
 *     {
 *       key: 'status',
 *       header: 'Status',
 *       type: 'text',
 *       format: (value) => (
 *         <Badge variant={value === 'active' ? 'success' : 'error'}>
 *           {value}
 *         </Badge>
 *       )
 *     }
 *   ]}
 *   data={statusData}
 * />
 */
export function DataGrid<T extends DataGridBaseRow>(props: DataGridProps<T>) {
	const [navigationRevision, setNavigationRevision] = React.useState(0);
	const handleExternalNavigation = React.useCallback(() => setNavigationRevision(revision => revision + 1), []);

	return <DataGridView<T> key={navigationRevision} {...props} onExternalNavigation={handleExternalNavigation} />;
}

interface IDataGridViewProps<T extends DataGridBaseRow> extends DataGridProps<T> {
	/**
	 * Called when the URL changes this grid's list state from outside (back/forward, an address-bar
	 * edit). `DataGrid` remounts the view so it hydrates from the new URL exactly as it does on
	 * landing, rather than patching live state while the route is mid-render.
	 */
	onExternalNavigation: () => void;
}

function DataGridView<T extends DataGridBaseRow>({
	actionBarProps,
	apiArgs,
	apiMethod,
	apiResponseParser,
	columns: tableColumns,
	data: tableData = [],
	defaultSortColumn,
	defaultSortDirection = OrderByEnum.DESC,
	meta: outsideMeta = DEFAULT_META,
	options: outsideOptions = {},
	pageSize: outsidePageSize,
	rowCount,
	rowSelection,
	strings,
	uniqueField = 'id',
	disableRowSelection,
	initialSelectedRowIds,
	withSelectAll,
	onError,
	onLoadingChange,
	onPaginationChange,
	onRefetchChange,
	onSortingChange,
	onSelectRows,
	onRowClick,
	onStateChange,
	filtersFetcher,
	rowsHeight = 'md',
	urlSync,
	dateRange,
	defaultDateRange,
	onDateRangeChange,
	onExternalNavigation,
}: IDataGridViewProps<T>) {
	const options = React.useMemo(() => {
		return {
			...DEFAULT_OPTIONS,
			...outsideOptions,
		};
	}, [outsideOptions.shrinkToFit, outsideOptions.locale, outsideOptions.timezone]);

	const {
		initialState: initialUrlState,
		commit: commitUrlState,
		subscribeToExternalNavigation,
	} = useDataGridUrlSync<T>({
		columns: tableColumns,
		defaultSortColumn,
		defaultSortDirection,
		urlSync,
		defaultDateRange,
	});

	const tableRef = React.useRef<HTMLTableElement>(null);
	// `null` until the grid measures its real position; `pageSize` falls back to a default purely
	// for layout sizing while the fetch stays gated until the measured size lands (see useDataGrid).
	const { pageSize: measuredPageSize, remeasure: remeasurePageSize } = useOptimalPageSize(
		rowsHeight,
		tableRef,
		outsidePageSize
	);
	const pageSize = measuredPageSize ?? outsidePageSize ?? DEFAULT_PAGE_SIZE;
	const [selectedRowsMetadata, setSelectedRowsMetadata] = React.useState<DataGridSelectedRowsMetadata<T>>({
		all: false,
		selectedRows: [],
		excludedIds: [],
		filters: {},
		selectedRowsCount: 0,
		totalRowsCount: 0,
		search: '',
	});
	const onSelectRowsRef = React.useRef(onSelectRows);

	const {
		activeFilters,
		columns,
		data,
		error,
		handleColumnVisibility,
		handleFiltersChange,
		handleClearFilters,
		handleHideColumn,
		handleSearch,
		handleSelectRow,
		handleSelectAllRows,
		handleSetPagination,
		handleSetSorting,
		handleSortAsc,
		handleSortDesc,
		applyMeasuredPageSize,
		hiddenColumns,
		isAllPagesSelected,
		isLoading,
		isInitialized,
		meta,
		pagination,
		refetch,
		searchQuery,
		selectedRows,
		selectedRowCount,
		sorting,
		sortValues,
		filterStates,
		filterableColumns,
	} = useDataGrid<T>({
		apiArgs,
		apiMethod,
		apiResponseParser,
		columns: tableColumns,
		data: tableData,
		defaultSortColumn,
		defaultSortDirection,
		initialSelectedRowIds,
		initialState: initialUrlState,
		meta: outsideMeta,
		options,
		pageSize: measuredPageSize,
		uniqueField,
		filtersFetcher,
	});

	const onDateRangeChangeRef = React.useRef(onDateRangeChange);
	React.useEffect(() => {
		onDateRangeChangeRef.current = onDateRangeChange;
	}, [onDateRangeChange]);

	React.useEffect(
		() => subscribeToExternalNavigation(onExternalNavigation),
		[onExternalNavigation, subscribeToExternalNavigation]
	);

	const hasHydratedDateRangeRef = React.useRef(false);
	React.useEffect(() => {
		if (hasHydratedDateRangeRef.current || initialUrlState == null) {
			return;
		}
		hasHydratedDateRangeRef.current = true;
		if (!initialUrlState.dateFrom && !initialUrlState.dateTo) {
			return;
		}
		onDateRangeChangeRef.current?.({
			startDate: initialUrlState.dateFrom ?? '',
			endDate: initialUrlState.dateTo ?? '',
		});
	}, [initialUrlState]);

	React.useEffect(() => {
		commitUrlState({
			activeFilters,
			hiddenColumns,
			pageIndex: pagination.pageIndex,
			searchQuery,
			sortColumn: sortValues.orderBy,
			sortDirection: sortValues.order,
			dateFrom: dateRange ? toUrlCalendarDate(dateRange.startDate, urlSync?.calendarDates) : undefined,
			dateTo: dateRange ? toUrlCalendarDate(dateRange.endDate, urlSync?.calendarDates) : undefined,
		});
	}, [
		activeFilters,
		commitUrlState,
		dateRange,
		hiddenColumns,
		pagination.pageIndex,
		searchQuery,
		sortValues.order,
		sortValues.orderBy,
		urlSync?.calendarDates,
	]);

	const actionBarRef = React.useRef<HTMLDivElement>(null);

	React.useEffect(() => {
		const element = actionBarRef.current;
		if (!element) return;

		const resizeObserver = new ResizeObserver(() => remeasurePageSize());
		resizeObserver.observe(element);

		return () => resizeObserver.disconnect();
	}, [remeasurePageSize]);

	const handleOnPaginationChange: OnChangeFn<PaginationState> = (updaterOrValue: Updater<PaginationState>) => {
		const newPagination =
			typeof updaterOrValue === 'function' ? updaterOrValue(table.getState().pagination) : updaterOrValue;

		handleSetPagination(newPagination);
		onPaginationChange?.(newPagination);
	};

	const handleOnSortingChange: OnChangeFn<SortingState> = (updaterOrValue: Updater<SortingState>) => {
		const newSorting = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;

		handleSetSorting(newSorting);
		onSortingChange?.(newSorting);

		// Reset pagination to first page when sort changes
		if (
			sorting.length > 0 &&
			newSorting.length > 0 &&
			(sorting[0].id !== newSorting[0].id || sorting[0].desc !== newSorting[0].desc)
		) {
			const resetPagination = {
				...pagination,
				pageIndex: 0, // Reset to first page
			};

			handleSetPagination(resetPagination);
			onPaginationChange?.(resetPagination);
		}
	};

	// Prepend a checkbox column if rowSelection is active
	const columnsWithSelection = React.useMemo<ColumnDef<DataGridRow<T>>[]>(() => {
		if (!rowSelection || (rowSelection && rowSelection === 'single')) return columns;

		const selectionColumn = createSelectionColumn<T>({
			selectedRows,
			selectedRowCount,
			isAllPagesSelected,
			meta,
			pagination,
			handleSelectRow,
			handleSelectAllRows: withSelectAll ? handleSelectAllRows : undefined,
			disableRowSelection,
			uniqueField,
		});

		return [selectionColumn, ...columns];
	}, [
		columns,
		isAllPagesSelected,
		meta,
		pagination,
		rowSelection,
		selectedRows,
		selectedRowCount,
		handleSelectRow,
		handleSelectAllRows,
		withSelectAll,
	]);

	const dataWithSelectionState: DataGridRow<T>[] = React.useMemo(() => {
		return data.map(row => {
			const rowId = row[uniqueField] as string;
			const pageIndex = pagination.pageIndex.toString();
			const currentPageSelections = selectedRows[pageIndex] || {};

			// Determine if this row is selected
			const { isDisabled, tooltipMessage } = resolveRowSelectionDisabled(disableRowSelection, row);

			const isSelected = isRowSelected({
				currentPageSelections,
				isAllPagesSelected,
				rowId,
				isDisabled,
			});

			// Add selection state to the row data
			return {
				...row,
				// Use a property name unlikely to conflict with actual data (triggers cell re-render)
				__selectedState: isSelected,
				__isDisabled: isDisabled,
				__disabledTooltip: tooltipMessage,
			};
		});
	}, [data, uniqueField, pagination.pageIndex, selectedRows, isAllPagesSelected, disableRowSelection]);

	const table = useReactTable({
		data: dataWithSelectionState,
		columns: columnsWithSelection,
		rowCount,
		state: {
			pagination,
			rowSelection: selectedRows[pagination.pageIndex] || {}, // Only pass the current page's selection
			sorting,
		},
		manualSorting: false,
		manualPagination: true,
		enableMultiRowSelection: Boolean(rowSelection === 'multiple'),
		enableRowSelection: Boolean(rowSelection),
		onPaginationChange: handleOnPaginationChange,
		onSortingChange: handleOnSortingChange,
		pageCount: meta.totalPages,
		getCoreRowModel: getCoreRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getSubRows: row => (row as any).subRows, // return the children array as sub-rows
		enableColumnResizing: true,
		columnResizeMode: 'onChange', // Update columns widths as they are being resized
	});

	const headerGroups = table.getHeaderGroups() ?? [];
	const tableRows = table.getRowModel().rows ?? [];

	const debouncedSetPagination = useDebounce(applyMeasuredPageSize, PAGE_SIZE_SYNC_DEBOUNCE_MS);

	React.useEffect(() => {
		if (measuredPageSize == null) {
			return;
		}
		// Apply even when the count already matches the placeholder. Skipping that call leaves the
		// first-measurement flag unset, so the next resize keeps a restored page instead of
		// returning to the first page.
		if (pagination.pageSize === measuredPageSize) {
			applyMeasuredPageSize(measuredPageSize);
			return;
		}
		// The fetch stays gated until pagination matches the measured size, so the placeholder
		// size is never requested.
		debouncedSetPagination(measuredPageSize);
	}, [applyMeasuredPageSize, debouncedSetPagination, measuredPageSize, pagination.pageSize]);

	// Update ref when callback changes
	React.useEffect(() => {
		onSelectRowsRef.current = onSelectRows;
	}, [onSelectRows]);

	// Effect to handle row selection changes
	React.useEffect(() => {
		const tableRows = table.getRowModel().rows;
		const selectedRowsData = getSelectedRows(
			selectedRows,
			tableRows,
			selectedRowsMetadata.selectedRows,
			isAllPagesSelected,
			uniqueField,
			activeFilters,
			getActionableItemCount(meta),
			searchQuery
		);

		setSelectedRowsMetadata(selectedRowsData);
		onSelectRowsRef.current?.(selectedRowsData);
	}, [
		data,
		isAllPagesSelected,
		meta.totalItems,
		meta.actionableTotalItems,
		rowSelection,
		selectedRowCount,
		selectedRows,
		table,
		searchQuery,
	]);

	// Effect to expose refetch to parent
	React.useEffect(() => {
		onRefetchChange?.(() => Promise.resolve(refetch()));
	}, [onRefetchChange, refetch]);

	// Effect to handle loading state changes
	React.useEffect(() => {
		onLoadingChange?.(isLoading);
	}, [isLoading, onLoadingChange]);

	// Effect to handle error state changes
	React.useEffect(() => {
		if (error) {
			onError?.(error);
		}
	}, [error, onError]);

	// Effect to handle state changes
	React.useEffect(() => {
		onStateChange?.({
			activeFilters,
			columns,
			data,
			error,
			hiddenColumns,
			isAllPagesSelected,
			isLoading,
			pagination,
			searchQuery,
			selectedRows,
			selectedRowCount,
			sorting,
		});
	}, [
		activeFilters,
		columns,
		data,
		error,
		hiddenColumns,
		isAllPagesSelected,
		isLoading,
		onStateChange,
		pagination,
		searchQuery,
		selectedRows,
		selectedRowCount,
		sorting,
	]);

	const hasVisibleColumns = columns.length > 0;
	const minPageSize = options.shrinkToFit && meta.totalPages === 1 ? meta.totalItems : pageSize;
	const tableMinHeight =
		options.shrinkToFit && meta.totalItems === 0
			? EMPTY_STATE_MIN_HEIGHT_PX
			: `${minPageSize * getRowsMinHeight(rowsHeight) + HEADER_HEIGHT}px`;

	const handleRowClick: (row: DataGridRow<T>) => void | undefined = React.useMemo(() => {
		if (onRowClick || onRowClick === null) {
			return onRowClick;
		}

		if (rowSelection) {
			return row => (!row.__isDisabled ? handleSelectRow(row[uniqueField] as string, !row.__selectedState) : undefined);
		}

		return row => {
			if (row.__isDisabled) {
				return;
			}
			const actionsColumn = tableColumns.find(column => column.type === 'actions') as DataGridActionColumnDef<T>;
			if (actionsColumn?.actionsUsability) {
				const usabilityResult = actionsColumn.actionsUsability(row);
				if (usabilityResult !== true) return;
			}
			const firstAction = actionsColumn?.actions?.[0];
			const isUsable = firstAction?.isUsable ? firstAction.isUsable(row) : true;
			if (isUsable === true) firstAction?.onClick?.(row);
		};
	}, [onRowClick, tableColumns, rowSelection, handleSelectRow, uniqueField]);

	const handleActionBarClearFilters = React.useCallback(() => {
		handleClearFilters();
		actionBarProps?.onClearFilters?.();
	}, [actionBarProps?.onClearFilters, handleClearFilters]);

	return (
		<>
			{actionBarProps && (
				<div ref={actionBarRef}>
					<ActionBar<T>
						apiArgs={apiArgs}
						columns={tableColumns}
						onFiltersChange={handleFiltersChange}
						handleClearFilters={handleActionBarClearFilters}
						selectedFilters={activeFilters}
						hiddenColumns={hiddenColumns}
						onColumnVisibilityChange={handleColumnVisibility}
						onSearch={handleSearch}
						searchQuery={searchQuery}
						selectionMetadata={selectedRowsMetadata}
						filterStates={filterStates}
						filterableColumns={filterableColumns}
						strings={strings}
						{...actionBarProps}
					/>
				</div>
			)}
			<TablePrimitiveContainer>
				<TablePrimitive ref={tableRef} minHeight={tableMinHeight} data-aid="data-grid">
					{!isInitialized && <LoadingOverlay strings={strings} />}
					{isInitialized && !isLoading && !hasVisibleColumns && <NoColumnsOverlay strings={strings} />}
					<DataGridHeader
						headerGroups={headerGroups}
						handleHideColumn={handleHideColumn}
						handleSortAsc={handleSortAsc}
						handleSortDesc={handleSortDesc}
						resizingColumnId={table.getState().columnSizingInfo.isResizingColumn || ''}
					/>
					<DataGridBody
						columns={columns}
						hasRowDisablement={Boolean(disableRowSelection)}
						isLoading={isInitialized && isLoading}
						onRowClick={handleRowClick}
						pageSize={pageSize}
						rowsHeight={rowsHeight}
						rowSelection={rowSelection}
						strings={strings}
						tableMinHeight={tableMinHeight}
						tableRows={tableRows}
					/>
				</TablePrimitive>
				<DataGridFooter
					meta={meta}
					pagination={pagination}
					selectedRowCount={selectedRowCount}
					strings={strings}
					table={table}
					tableRows={tableRows}
				/>
			</TablePrimitiveContainer>
		</>
	);
}
